/**
 * Draft step scripts from the CRM preview frame (#TAG-3352).
 *
 * The CRM shows a page next to its Scripts editor and wants it to run the
 * merchant's UNSAVED scripts. Step scripts reach a live page baked into the
 * route at publish (`__TGD_STEP_CONFIG__`), so the page asks its parent for
 * the draft list instead, before the injectors add anything:
 *
 *   page → parent  { type: 'TAGADAPAY_SCRIPTS_REQUEST' }
 *   parent → page  { type: 'TAGADAPAY_SCRIPTS_PREVIEW', scripts: [{ name, enabled, content, position }] }
 *   page → parent  { type: 'TAGADAPAY_SCRIPT_ERROR', index, message, line }
 *   page → parent  { type: 'TAGADAPAY_CONSOLE', … }  (scriptsPreviewConsole.ts, #REW-974)
 *
 * Only when the URL carries `scriptsPreview=1`, the page is framed, and the
 * answer comes from the parent window on a CRM origin. Accepting scripts from
 * any framer would let any site run its code on the merchant's domain. No
 * answer in time → the published scripts, exactly as today.
 */

import type { RuntimeStepConfig } from '../funnelClient';
import { settleConsoleRelay, startConsoleRelay, stopConsoleRelay, type ConsoleSource } from './scriptsPreviewConsole';

export type StepScript = NonNullable<RuntimeStepConfig['scripts']>[number];

export const SCRIPTS_PREVIEW_PARAM = 'scriptsPreview';
export const SCRIPTS_PREVIEW_TIMEOUT_MS = 1500;
export const SCRIPTS_PREVIEW_MESSAGES = {
  request: 'TAGADAPAY_SCRIPTS_REQUEST',
  preview: 'TAGADAPAY_SCRIPTS_PREVIEW',
  error: 'TAGADAPAY_SCRIPT_ERROR',
} as const;

/** The CRM V3 deployments allowed to hand a page its scripts. */
export const SCRIPTS_PREVIEW_ORIGINS: readonly string[] = ['https://v3.tagada.io', 'https://v3.tagada.dev'];

const POSITIONS = ['head-start', 'head-end', 'body-start', 'body-end'];

type PreviewWindow = Pick<Window, 'parent' | 'location' | 'addEventListener' | 'removeEventListener'> & {
  TagadaPay?: Record<string, unknown>;
  console?: Partial<Record<'log' | 'info' | 'warn' | 'error' | 'debug', (...args: unknown[]) => void>>;
};

let draftScripts: StepScript[] | undefined;
let parentOrigin: string | null = null;
let pending: Promise<void> | null = null;
/** `index-elemIndex` → what to add to a line of the wrapped inline script to get the merchant's line. */
const lineDeltas = new Map<string, number>();

export function isAllowedPreviewOrigin(origin: string): boolean {
  if (SCRIPTS_PREVIEW_ORIGINS.includes(origin)) return true;
  try {
    const url = new URL(origin);
    return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
  } catch {
    return false;
  }
}

export function isScriptsPreview(win: PreviewWindow | undefined = typeof window === 'undefined' ? undefined : window): boolean {
  if (!win) return false;
  try {
    return win.parent !== (win as unknown) && new URLSearchParams(win.location.search).get(SCRIPTS_PREVIEW_PARAM) === '1';
  } catch {
    return false;
  }
}

/** The draft list from the CRM, once accepted; `undefined` means "use the published scripts". */
export function getDraftScripts(): StepScript[] | undefined {
  return draftScripts;
}

/** The scripts of a `TAGADAPAY_SCRIPTS_PREVIEW` message, or null when it is not one we accept. */
export function readPreviewScripts(data: unknown): StepScript[] | null {
  if (!data || typeof data !== 'object') return null;
  const message = data as { type?: unknown; scripts?: unknown };
  if (message.type !== SCRIPTS_PREVIEW_MESSAGES.preview || !Array.isArray(message.scripts)) return null;
  const scripts: StepScript[] = [];
  for (const item of message.scripts) {
    if (!item || typeof item !== 'object') return null;
    const { name, enabled, content, position } = item as Record<string, unknown>;
    if (typeof name !== 'string' || typeof enabled !== 'boolean' || typeof content !== 'string') return null;
    if (position !== undefined && !POSITIONS.includes(position as string)) return null;
    scripts.push({ name, enabled, content, position: position as StepScript['position'] });
  }
  return scripts;
}

/** Publish `TagadaPay.isPreview`, so a merchant script can skip itself in the CRM preview. */
export function publishPreviewFlag(win: PreviewWindow | undefined = typeof window === 'undefined' ? undefined : window): void {
  if (!win) return;
  win.TagadaPay = { ...(win.TagadaPay ?? {}), isPreview: isScriptsPreview(win) };
}

/**
 * Ask the parent for the draft scripts. Resolves once they are accepted or the
 * wait is over; resolves at once outside a scripts preview. Asked once per page.
 */
export function requestDraftScripts(
  win: PreviewWindow | undefined = typeof window === 'undefined' ? undefined : window,
  timeoutMs = SCRIPTS_PREVIEW_TIMEOUT_MS,
): Promise<void> {
  if (pending) return pending;
  if (!win || !isScriptsPreview(win)) {
    pending = Promise.resolve();
    return pending;
  }
  // The page's console goes up to the CRM too, held until it answers (#REW-974).
  startConsoleRelay(win, scriptOfStack);
  pending = new Promise<void>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== win.parent || !isAllowedPreviewOrigin(event.origin)) return;
      const scripts = readPreviewScripts(event.data);
      if (!scripts) return;
      draftScripts = scripts;
      parentOrigin = event.origin;
      done();
    };
    const done = () => {
      win.removeEventListener('message', onMessage as EventListener);
      if (timer !== undefined) clearTimeout(timer);
      settleConsoleRelay(parentOrigin);
      resolve();
    };
    win.addEventListener('message', onMessage as EventListener);
    timer = setTimeout(done, timeoutMs);
    // The request carries no data, so any target is fine; the answer's origin is what we check.
    win.parent.postMessage({ type: SCRIPTS_PREVIEW_MESSAGES.request }, '*');
  });
  return pending;
}

/** The name an inline script runs under in the preview, so its errors can be traced back to it. */
export function previewSourceName(index: number, elemIndex: number): string {
  return `tagada-script-${index}-${elemIndex}.js`;
}

/**
 * What the preview appends to a wrapped inline script: inside its `catch`, the
 * hook that reports the error; at the end, the name its errors carry. Empty
 * strings outside a scripts preview, so live pages run the same code as before.
 *
 * `codeLine` is the line of the wrapper where the merchant code starts, and
 * `contentLine` the line of `script.content` that code starts at.
 */
export function previewWrapperParts(
  index: number,
  elemIndex: number,
  codeLine: number,
  contentLine: number,
): { catchHook: string; sourceUrl: string } {
  if (!draftScripts) return { catchHook: '', sourceUrl: '' };
  lineDeltas.set(`${index}-${elemIndex}`, contentLine - codeLine);
  return {
    catchHook: `\n  if (window.TagadaPay && window.TagadaPay._scriptError) window.TagadaPay._scriptError(${index}, ${elemIndex}, error);`,
    sourceUrl: `\n//# sourceURL=${previewSourceName(index, elemIndex)}`,
  };
}

/** The 1-based line of `content` where `code` (a piece of it) starts. */
export function lineOfCode(content: string, code: string): number {
  const at = content.indexOf(code);
  return at < 0 ? 1 : content.slice(0, at).split('\n').length;
}

/** The merchant's line for a line of a wrapped inline script, when known. */
export function merchantLine(index: number, elemIndex: number, wrapperLine: number | undefined): number | undefined {
  const delta = lineDeltas.get(`${index}-${elemIndex}`);
  if (delta === undefined || !wrapperLine) return undefined;
  const line = wrapperLine + delta;
  return line > 0 ? line : undefined;
}

/** The draft script the innermost frame of a stack belongs to, and the merchant's line in it. */
export function scriptOfStack(stack: string | undefined): ConsoleSource | null {
  const match = /tagada-script-(\d+)-(\d+)\.js:(\d+)/.exec(stack ?? '');
  if (!match) return null;
  const index = Number(match[1]);
  return { index, line: merchantLine(index, Number(match[2]), Number(match[3])) ?? null };
}

/** The wrapper line an error was thrown at, from its stack. */
export function wrapperLineFromStack(stack: string | undefined, index: number, elemIndex: number): number | undefined {
  if (!stack) return undefined;
  const name = previewSourceName(index, elemIndex).replace(/\./g, '\\.');
  const match = new RegExp(`${name}:(\\d+)`).exec(stack);
  return match ? Number(match[1]) : undefined;
}

function postError(win: PreviewWindow, index: number, message: string, line: number | undefined): void {
  if (!parentOrigin) return;
  win.parent.postMessage({ type: SCRIPTS_PREVIEW_MESSAGES.error, index, message, line: line ?? null }, parentOrigin);
}

/**
 * Report the draft scripts' errors to the CRM: the ones thrown while a script
 * runs (its `catch` calls the hook) and the later ones, thrown from its
 * listeners and timers (the window `error` event, matched on the script's name).
 */
export function installScriptErrorRelay(win: PreviewWindow | undefined = typeof window === 'undefined' ? undefined : window): void {
  if (!win || !draftScripts) return;
  win.TagadaPay = {
    ...(win.TagadaPay ?? {}),
    _scriptError: (index: number, elemIndex: number, error: unknown) => {
      const stack = error instanceof Error ? error.stack : undefined;
      const message = error instanceof Error ? error.message : String(error);
      postError(win, index, message, merchantLine(index, elemIndex, wrapperLineFromStack(stack, index, elemIndex)));
    },
  };
  win.addEventListener('error', ((event: ErrorEvent) => {
    const match = /tagada-script-(\d+)-(\d+)\.js/.exec(event.filename || '');
    if (!match) return;
    const index = Number(match[1]);
    const elemIndex = Number(match[2]);
    postError(win, index, event.message, merchantLine(index, elemIndex, event.lineno));
  }) as EventListener);
}

/** Forget the exchange. Tests only. */
export function resetScriptsPreview(): void {
  draftScripts = undefined;
  parentOrigin = null;
  pending = null;
  lineDeltas.clear();
  stopConsoleRelay();
}
